/**
 * Utility functions for Cal frontend
 */

/**
 * Format a date for display in user's timezone (or specified TZ)
 */
export function fmtDate(dateStr, timeZone, options = {}) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      timeZone: timeZone || undefined,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...options,
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format a time for display
 */
export function fmtTime(dateStr, timeZone) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      timeZone: timeZone || undefined,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format a date+time
 */
export function fmtDateTime(dateStr, timeZone) {
  if (!dateStr) return '';
  const d = fmtDate(dateStr, timeZone);
  const t = fmtTime(dateStr, timeZone);
  return `${d} at ${t}`;
}

/**
 * Get week start (Monday) for a given date
 */
export function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday = 0 offset
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get array of 7 days starting from a date
 */
export function getWeekDays(weekStart) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

/**
 * Is two dates the same calendar day?
 */
export function isSameDay(a, b) {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate();
}

/**
 * Is a date today?
 */
export function isToday(date) {
  return isSameDay(date, new Date());
}

/**
 * Is a date in the past?
 */
export function isPast(date) {
  return new Date(date) < new Date();
}

/**
 * Generate an .ics file content for a booking
 */
export function generateICS({ summary, description, start, end, location }) {
  const fmt = (d) => new Date(d).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ForgeLine Cal//Cal Booking//EN',
    'BEGIN:VEVENT',
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end || new Date(new Date(start).getTime() + 3600000))}`,
    `SUMMARY:${summary || 'Booking'}`,
    `DESCRIPTION:${(description || '').replace(/\n/g, '\\n')}`,
    location ? `LOCATION:${location}` : '',
    `UID:${Date.now()}@cal.forgelinesystems.com`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');
}

/**
 * Download a blob as a file
 */
export function downloadBlob(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Abbreviate timezone for display
 */
export function tzAbbr(tz) {
  if (!tz) return '';
  try {
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' });
    const parts = fmt.formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || tz;
  } catch {
    return tz;
  }
}

/**
 * Copy text to clipboard, return success bool
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Days of week labels (Mon-Sun)
 */
export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DAY_FULL_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
